import { Capacitor, registerPlugin } from '@capacitor/core';

/**
 * Native WKWebView screenshot bridge.
 *
 * The Route share template renders Apple MapKit JS in a WebGL canvas which
 * `html2canvas` can't capture (cross-origin tainted canvas). The native
 * snapshot, on the other hand, asks WebKit itself to render the WebView's
 * compositor output - same path it'd use to take a system screenshot - so
 * the live map ends up in the captured image.
 *
 * Falls back gracefully to `null` on web/Android so callers can branch and
 * use html2canvas instead.
 */

interface CaptureRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface CaptureOpts {
  /** Optional clipping rect, in CSS points relative to the WebView. */
  rect?: CaptureRect;
  /**
   * Optional output width override, in points. WebKit will scale the captured
   * area so that its width matches this value (height scales to preserve the
   * aspect ratio of `rect`). Useful when the JS template is laid out larger
   * than the visible viewport and we want a fixed-size share image.
   */
  snapshotWidth?: number;
}

interface CaptureResult {
  base64: string;
  width: number;
  height: number;
}

interface StraivScreenshotPlugin {
  captureWebView(opts: CaptureOpts): Promise<CaptureResult>;
}

const StraivScreenshot = registerPlugin<StraivScreenshotPlugin>('StraivScreenshot');

export function isNativeScreenshotAvailable(): boolean {
  return Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios';
}

/**
 * Capture the current WebView. Returns a `data:image/png;base64,...` URL on
 * success or `null` if the platform doesn't support it / the capture fails.
 */
export async function captureWebViewAsDataUrl(
  opts: CaptureOpts = {}
): Promise<string | null> {
  if (!isNativeScreenshotAvailable()) return null;
  try {
    const result = await StraivScreenshot.captureWebView(opts);
    if (!result?.base64) return null;
    return `data:image/png;base64,${result.base64}`;
  } catch (err) {
    console.warn('[StraivScreenshot] capture failed:', err);
    return null;
  }
}
